import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Trophy, Calendar, Star, AlertTriangle, Target } from 'lucide-react';

// Milestone types and their icons
const MILESTONE_ICONS = {
  world_cup: Trophy,
  tournament: Trophy,
  coaching_change: Star,
  sponsorship: Target,
  achievement: Star,
  setback: AlertTriangle,
};

const MILESTONE_COLORS = {
  world_cup: 'bg-stable/20 text-stable border-stable/30',
  tournament: 'bg-primary/20 text-primary border-primary/30',
  coaching_change: 'bg-warning/20 text-warning border-warning/30',
  sponsorship: 'bg-stable/20 text-stable border-stable/30',
  achievement: 'bg-stable/20 text-stable border-stable/30',
  setback: 'bg-destructive/20 text-destructive border-destructive/30',
};

// Generate milestone data from cycle info
function generateMilestones(currentCycle, historicalCycles) {
  const milestones = [];
  
  if (historicalCycles && historicalCycles.length > 0) {
    historicalCycles.forEach((cycle) => {
      const startYear = parseInt(cycle.years.split('-')[0]);
      const endYear = parseInt(cycle.years.split('-')[1]);
      
      milestones.push({
        year: startYear,
        type: 'tournament',
        label: `${cycle.cycle_name} Start`,
        brandIndex: cycle.brand_index_start,
        description: `Cycle began with brand index at ${cycle.brand_index_start}`,
      });
      
      const isGood = cycle.world_cup_result !== 'Did Not Qualify';
      milestones.push({
        year: endYear,
        type: isGood ? 'world_cup' : 'setback',
        label: `World Cup ${endYear}`,
        brandIndex: cycle.brand_index_end,
        description: cycle.world_cup_result,
        result: cycle.world_cup_result,
      });
    });
  }
  
  if (currentCycle) {
    milestones.push({
      year: currentCycle.start_year,
      type: 'tournament',
      label: `${currentCycle.cycle_name} Start`,
      brandIndex: null,
      description: `New cycle began`,
    });
    
    currentCycle.key_tournaments?.forEach(tournament => {
      const type = tournament.result.includes('Champion') || tournament.result.includes('Gold') 
        ? 'achievement' 
        : tournament.result.includes('Upcoming') 
          ? 'world_cup'
          : 'tournament';
      
      milestones.push({
        year: tournament.year,
        type,
        label: tournament.name,
        description: tournament.result,
        result: tournament.result,
        isUpcoming: tournament.result.includes('Upcoming'),
      });
    });
  }
  
  return milestones.sort((a, b) => a.year - b.year);
}

// Calculate brand evolution data points
function generateEvolutionData(historicalCycles, currentBrandScore, currentYear = 2026) {
  const dataPoints = [];
  
  if (historicalCycles && historicalCycles.length > 0) {
    historicalCycles.forEach(cycle => {
      const startYear = parseInt(cycle.years.split('-')[0]);
      const endYear = parseInt(cycle.years.split('-')[1]);
      
      dataPoints.push({ year: startYear, score: cycle.brand_index_start });
      dataPoints.push({ year: endYear, score: cycle.brand_index_end });
    });
  }
  
  if (currentBrandScore) {
    dataPoints.push({ year: currentYear, score: currentBrandScore });
  }
  
  return dataPoints.sort((a, b) => a.year - b.year);
}

export default function CycleEvolutionChart({ currentCycle, historicalCycles, brandMetrics, teamName }) {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  
  if (!currentCycle) return null;
  
  const currentBrandScore = brandMetrics?.overall_brand_score || 70;
  const evolutionData = generateEvolutionData(historicalCycles, currentBrandScore);
  const milestones = generateMilestones(currentCycle, historicalCycles);
  
  // Fixed SVG dimensions for proper rendering
  const svgWidth = 600;
  const svgHeight = 280;
  const padding = { top: 40, right: 40, bottom: 50, left: 60 };
  const chartWidth = svgWidth - padding.left - padding.right;
  const chartHeight = svgHeight - padding.top - padding.bottom;
  
  // Calculate data ranges
  const minYear = Math.min(...evolutionData.map(d => d.year), currentCycle.start_year - 4);
  const maxYear = Math.max(...evolutionData.map(d => d.year), currentCycle.end_year);
  const yearRange = maxYear - minYear || 1;
  
  const allScores = evolutionData.map(d => d.score);
  const minScore = Math.floor((Math.min(...allScores) - 5) / 10) * 10;
  const maxScore = Math.ceil((Math.max(...allScores) + 5) / 10) * 10;
  const scoreRange = maxScore - minScore || 1;
  
  // Scale functions
  const scaleX = (year) => padding.left + ((year - minYear) / yearRange) * chartWidth;
  const scaleY = (score) => padding.top + chartHeight - ((score - minScore) / scoreRange) * chartHeight;
  
  // Generate smooth path
  const generatePath = () => {
    if (evolutionData.length < 2) return '';
    return evolutionData.map((point, i) => {
      const x = scaleX(point.year);
      const y = scaleY(point.score);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');
  };
  
  // Generate Y-axis ticks
  const yTicks = [];
  for (let score = minScore; score <= maxScore; score += 10) {
    yTicks.push(score);
  }
  
  // Generate X-axis ticks (every 2 years)
  const xTicks = [];
  for (let year = Math.ceil(minYear / 2) * 2; year <= maxYear; year += 2) {
    xTicks.push(year);
  }
  
  // Determine trend
  const lastTwo = evolutionData.slice(-2);
  const isUpward = lastTwo.length === 2 && lastTwo[1].score > lastTwo[0].score;
  
  return (
    <Card className="bg-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            <span>4-Year Brand Evolution</span>
          </div>
          <div className="flex items-center gap-2">
            {isUpward ? (
              <Badge className="bg-stable/10 text-stable border-stable/30">
                <TrendingUp className="w-3 h-3 mr-1" />
                Upward Trend
              </Badge>
            ) : (
              <Badge className="bg-warning/10 text-warning border-warning/30">
                <TrendingDown className="w-3 h-3 mr-1" />
                Recovery Phase
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Evolution Chart */}
        <div className="relative bg-muted/20 rounded-lg p-4">
          <div className="w-full overflow-hidden" style={{ minHeight: '280px' }}>
            <svg 
              viewBox={`0 0 ${svgWidth} ${svgHeight}`} 
              className="w-full h-auto"
              preserveAspectRatio="xMidYMid meet"
              style={{ maxHeight: '320px' }}
            >
              {/* Background */}
              <rect 
                x={padding.left} 
                y={padding.top} 
                width={chartWidth} 
                height={chartHeight} 
                fill="transparent"
              />
              
              {/* Grid lines - horizontal */}
              {yTicks.map(score => {
                const y = scaleY(score);
                return (
                  <g key={`grid-${score}`}>
                    <line 
                      x1={padding.left} 
                      y1={y} 
                      x2={svgWidth - padding.right} 
                      y2={y} 
                      stroke="currentColor" 
                      strokeOpacity="0.1"
                      strokeDasharray="4,4"
                    />
                  </g>
                );
              })}
              
              {/* Grid lines - vertical */}
              {xTicks.map(year => {
                const x = scaleX(year);
                return (
                  <line 
                    key={`vgrid-${year}`}
                    x1={x} 
                    y1={padding.top} 
                    x2={x} 
                    y2={svgHeight - padding.bottom} 
                    stroke="currentColor" 
                    strokeOpacity="0.05"
                  />
                );
              })}
              
              {/* Y-axis labels */}
              {yTicks.map(score => {
                const y = scaleY(score);
                return (
                  <text 
                    key={`ylabel-${score}`}
                    x={padding.left - 12} 
                    y={y + 4} 
                    fontSize="12" 
                    fontFamily="system-ui, sans-serif"
                    fill="currentColor" 
                    opacity="0.6"
                    textAnchor="end"
                  >
                    {score}
                  </text>
                );
              })}
              
              {/* X-axis labels */}
              {xTicks.map(year => {
                const x = scaleX(year);
                return (
                  <text 
                    key={`xlabel-${year}`}
                    x={x} 
                    y={svgHeight - padding.bottom + 24} 
                    fontSize="12" 
                    fontFamily="system-ui, sans-serif"
                    fill="currentColor" 
                    opacity="0.6"
                    textAnchor="middle"
                  >
                    {year}
                  </text>
                );
              })}
              
              {/* Y-axis title */}
              <text 
                x={16} 
                y={svgHeight / 2} 
                fontSize="11" 
                fontFamily="system-ui, sans-serif"
                fill="currentColor" 
                opacity="0.5"
                textAnchor="middle"
                transform={`rotate(-90, 16, ${svgHeight / 2})`}
              >
                Brand Index
              </text>
              
              {/* X-axis title */}
              <text 
                x={svgWidth / 2} 
                y={svgHeight - 8} 
                fontSize="11" 
                fontFamily="system-ui, sans-serif"
                fill="currentColor" 
                opacity="0.5"
                textAnchor="middle"
              >
                Year
              </text>
              
              {/* Evolution line - gradient effect */}
              <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="1" />
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              {/* Area under the line */}
              <path 
                d={`${generatePath()} L ${scaleX(evolutionData[evolutionData.length - 1]?.year || maxYear)} ${svgHeight - padding.bottom} L ${scaleX(evolutionData[0]?.year || minYear)} ${svgHeight - padding.bottom} Z`}
                fill="url(#lineGradient)"
                opacity="0.1"
              />
              
              {/* Main evolution line */}
              <path 
                d={generatePath()} 
                fill="none" 
                stroke="hsl(var(--primary))" 
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                filter="url(#glow)"
              />
              
              {/* Data points */}
              {evolutionData.map((point, i) => {
                const x = scaleX(point.year);
                const y = scaleY(point.score);
                const isLast = i === evolutionData.length - 1;
                const isHovered = hoveredPoint === i;
                
                return (
                  <g 
                    key={`point-${point.year}-${i}`}
                    onMouseEnter={() => setHoveredPoint(i)}
                    onMouseLeave={() => setHoveredPoint(null)}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* Hover area */}
                    <circle 
                      cx={x} 
                      cy={y} 
                      r="15"
                      fill="transparent"
                    />
                    
                    {/* Outer glow for last point */}
                    {isLast && (
                      <circle 
                        cx={x} 
                        cy={y} 
                        r="12"
                        fill="hsl(var(--stable-green))"
                        opacity="0.2"
                      />
                    )}
                    
                    {/* Main point */}
                    <circle 
                      cx={x} 
                      cy={y} 
                      r={isLast ? 8 : isHovered ? 7 : 5}
                      fill={isLast ? "hsl(var(--stable-green))" : "hsl(var(--primary))"}
                      stroke={isLast ? "hsl(var(--stable-green))" : "hsl(var(--primary))"}
                      strokeWidth="2"
                      strokeOpacity="0.3"
                    />
                    
                    {/* Inner dot */}
                    <circle 
                      cx={x} 
                      cy={y} 
                      r={isLast ? 4 : 2}
                      fill="hsl(var(--background))"
                    />
                    
                    {/* Score label for last point */}
                    {isLast && (
                      <g>
                        <rect
                          x={x - 18}
                          y={y - 32}
                          width="36"
                          height="20"
                          rx="4"
                          fill="hsl(var(--stable-green))"
                        />
                        <text 
                          x={x} 
                          y={y - 18} 
                          fontSize="12" 
                          fontFamily="system-ui, sans-serif"
                          fontWeight="bold"
                          fill="hsl(var(--background))"
                          textAnchor="middle"
                        >
                          {point.score}
                        </text>
                      </g>
                    )}
                    
                    {/* Hover tooltip */}
                    {isHovered && !isLast && (
                      <g>
                        <rect
                          x={x - 45}
                          y={y - 55}
                          width="90"
                          height="46"
                          rx="6"
                          fill="hsl(var(--card))"
                          stroke="hsl(var(--border))"
                          strokeWidth="1"
                          filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
                        />
                        <text 
                          x={x} 
                          y={y - 38} 
                          fontSize="11" 
                          fontFamily="system-ui, sans-serif"
                          fill="currentColor"
                          opacity="0.7"
                          textAnchor="middle"
                        >
                          Year: {point.year}
                        </text>
                        <text 
                          x={x} 
                          y={y - 23} 
                          fontSize="13" 
                          fontFamily="system-ui, sans-serif"
                          fontWeight="bold"
                          fill="hsl(var(--primary))"
                          textAnchor="middle"
                        >
                          Score: {point.score}
                        </text>
                        {i > 0 && (
                          <text 
                            x={x} 
                            y={y - 10} 
                            fontSize="10" 
                            fontFamily="system-ui, sans-serif"
                            fill={evolutionData[i].score >= evolutionData[i-1].score ? "hsl(var(--stable-green))" : "hsl(var(--destructive))"}
                            textAnchor="middle"
                          >
                            {evolutionData[i].score >= evolutionData[i-1].score ? '+' : ''}
                            {((evolutionData[i].score - evolutionData[i-1].score) / evolutionData[i-1].score * 100).toFixed(1)}% YoY
                          </text>
                        )}
                      </g>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
          
          {/* Chart Legend */}
          <div className="flex justify-between items-center text-xs text-muted-foreground mt-3 pt-3 border-t border-border/50">
            <span className="flex items-center gap-2">
              <div className="w-3 h-0.5 bg-primary rounded" />
              Brand Index Score Over Time
            </span>
            <span className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-stable" />
              Current: <span className="font-semibold text-stable">{currentBrandScore}</span>
            </span>
          </div>
        </div>
        
        {/* Milestones Timeline */}
        <div>
          <h4 className="text-sm font-medium mb-3">Key Milestones</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {milestones.slice(-8).map((milestone, i) => {
              const Icon = MILESTONE_ICONS[milestone.type] || Trophy;
              const colorClass = MILESTONE_COLORS[milestone.type] || MILESTONE_COLORS.tournament;
              
              return (
                <div 
                  key={i} 
                  className={`flex items-center gap-3 p-2 rounded-lg border ${
                    milestone.isUpcoming ? 'border-dashed opacity-70' : ''
                  } ${colorClass}`}
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-background/50">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{milestone.label}</span>
                      <span className="text-xs text-muted-foreground">{milestone.year}</span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{milestone.description}</p>
                  </div>
                  {milestone.brandIndex && (
                    <div className="text-right">
                      <span className="text-sm font-bold">{milestone.brandIndex}</span>
                      <p className="text-xs text-muted-foreground">Index</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Cycle Summary */}
        <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border">
          <div className="text-center">
            <p className="text-2xl font-bold text-primary">{currentCycle.cycle_progress_pct}%</p>
            <p className="text-xs text-muted-foreground">Cycle Progress</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground">{currentCycle.end_year - currentCycle.start_year}</p>
            <p className="text-xs text-muted-foreground">Year Cycle</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-stable">{currentBrandScore}</p>
            <p className="text-xs text-muted-foreground">Current Score</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
